"use client";
import { useEffect, useMemo, useState } from "react";
import { orderBy } from "lodash";
import PromoService, {
  flattenShowOptions,
  PromoCode,
  PromoShowOption,
} from "@/app/_services/PromoService";
import { toast } from "@/app/_utils/toast";

export type PromoStatusFilter = "all" | "active" | "inactive";

export function usePromosModel() {
  const [promos, setPromos] = useState<PromoCode[]>([]);
  const [showOptions, setShowOptions] = useState<PromoShowOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<PromoStatusFilter>("all");
  const [selectedPromo, setSelectedPromo] = useState<PromoCode | null>(null);
  const [openForm, setOpenForm] = useState(false);
  // Show labels arrive in a second request, so the Shows column needs its own
  // loading state — otherwise it briefly renders a placeholder per unknown id.
  const [isShowMapLoading, setIsShowMapLoading] = useState(true);

  async function loadPromos() {
    setIsLoading(true);
    try {
      const res = await PromoService.getPromoCodesAsync();
      setPromos(orderBy(res, ["created_at"], ["desc"]));
    } catch {
      toast.error("Failed to load promo codes");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadPromos();
    // Fetched once here and shared with both the list (for show labels) and the form.
    PromoService.getUpcomingShowsAsync()
      .then((shows) => setShowOptions(flattenShowOptions(shows)))
      .catch(() => setShowOptions([]))
      .finally(() => setIsShowMapLoading(false));
  }, []);

  const showMap = useMemo(() => {
    const map: Record<string, string> = {};
    showOptions.forEach((option) => {
      map[option.id] = option.label;
    });
    return map;
  }, [showOptions]);

  const filteredPromos = useMemo(() => {
    const q = search.trim().toLowerCase();
    return promos.filter((promo) => {
      const matchesSearch = !q || promo.code.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "active" && promo.active) ||
        (statusFilter === "inactive" && !promo.active);
      return matchesSearch && matchesStatus;
    });
  }, [promos, search, statusFilter]);

  function openCreateForm() {
    setSelectedPromo(null);
    setOpenForm(true);
  }

  function openEditForm(promo: PromoCode) {
    setSelectedPromo(promo);
    setOpenForm(true);
  }

  function closeForm() {
    setOpenForm(false);
  }

  async function handleToggleActive(promo: PromoCode) {
    try {
      await PromoService.updatePromoCodeAsync(promo.id, { active: !promo.active });
      toast.success(promo.active ? "Promo code deactivated" : "Promo code activated");
      await loadPromos();
    } catch (e: any) {
      toast.error(e?.message || "Failed to update promo code");
    }
  }

  async function handleDelete(promo: PromoCode) {
    try {
      await PromoService.deletePromoCodeAsync(promo.id);
      toast.success("Promo code deleted");
      await loadPromos();
    } catch {
      toast.error("Failed to delete promo code");
    }
  }

  return {
    filteredPromos,
    showOptions,
    showMap,
    isShowMapLoading,
    isLoading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    selectedPromo,
    openForm,
    openCreateForm,
    openEditForm,
    closeForm,
    handleToggleActive,
    handleDelete,
    refetch: loadPromos,
  };
}
